import React from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { Close as CloseIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import type { WebMapLayerConfig } from '../types/webMap';

export interface MapLegendOverlayProps {
  layers: WebMapLayerConfig[];
  onClose: () => void;
  onToggleVisibility?: (layerId: string) => void;
}

function layerIcon(layer: WebMapLayerConfig, color?: string, icon?: string): React.ReactNode {
  if (layer.displayMode === 'heatmap') {
    return (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,0,0,0.8) 0%, rgba(255,165,0,0.4) 50%, rgba(0,128,255,0) 100%)',
          flexShrink: 0,
        }}
      />
    );
  }
  if (layer.displayMode === 'cluster') {
    return (
      <Box sx={{ position: 'relative', width: 18, height: 18, flexShrink: 0 }}>
        <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: layer.fillColor, opacity: 0.4, position: 'absolute' }} />
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: layer.fillColor, position: 'absolute', top: 4, left: 4 }} />
      </Box>
    );
  }
  if (icon ?? layer.mapboxIcon) {
    return (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          bgcolor: color ?? layer.fillColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: 10,
          color: '#fff',
          fontWeight: 700,
        }}
      >
        ●
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        bgcolor: color ?? layer.fillColor,
        border: `2px solid ${color ?? layer.strokeColor}`,
        flexShrink: 0,
      }}
    />
  );
}

const MapLegendOverlay: React.FC<MapLegendOverlayProps> = ({ layers, onClose, onToggleVisibility }) => {
  if (layers.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 40,
        left: 10,
        zIndex: 10,
        bgcolor: 'rgba(30,30,30,0.92)',
        backdropFilter: 'blur(6px)',
        borderRadius: 1,
        p: 1.5,
        minWidth: 180,
        maxWidth: 280,
        maxHeight: 300,
        overflow: 'auto',
        boxShadow: 3,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="caption" fontWeight={600} color="text.primary">
          Legend
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Stack>
      <Stack spacing={0.75}>
        {layers.map((lyr) => (
          <Box key={lyr.id} sx={{ opacity: lyr.visible ? 1 : 0.4 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {layerIcon(lyr)}
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ flex: 1, fontSize: '0.7rem', fontWeight: 600 }}
              >
                {lyr.sourceName || `${lyr.schema}.${lyr.table}`}
              </Typography>
              {onToggleVisibility && (
                <IconButton
                  size="small"
                  onClick={() => onToggleVisibility(lyr.id)}
                  sx={{ p: 0.25, opacity: 0.7, '&:hover': { opacity: 1 } }}
                >
                  {lyr.visible ? (
                    <Visibility sx={{ fontSize: 14 }} />
                  ) : (
                    <VisibilityOff sx={{ fontSize: 14 }} />
                  )}
                </IconButton>
              )}
            </Stack>
            {lyr.categoricalStyle && (
              <Stack spacing={0.5} sx={{ mt: 0.5, ml: 3.25 }}>
                {lyr.categoricalStyle.categories.map((category) => (
                  <Stack key={category.value} direction="row" alignItems="center" spacing={0.75}>
                    {layerIcon(lyr, category.color, category.icon)}
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.68rem' }}>
                      {category.label}
                    </Typography>
                  </Stack>
                ))}
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  {layerIcon(lyr, lyr.categoricalStyle.fallbackColor, lyr.categoricalStyle.fallbackIcon)}
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.68rem' }}>
                    Other / empty
                  </Typography>
                </Stack>
              </Stack>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default MapLegendOverlay;
